import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { ChevronLeft, ChevronDown, Search, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
  SheetClose
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useBankAccounts } from '@/hooks/useBankAccounts';
import { useClientPOs } from '@/hooks/useClientPOs';
import { useQuery } from '@tanstack/react-query';

export default function FinanceLedgerDetail() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const activeBank = searchParams.get('bank');
  const activeNameLower = activeBank === 'bca' ? 'apa saja bisa' : activeBank === 'mandiri' ? 'cuan jalan terus' : activeBank === 'bni' ? 'essential' : '';
  
  const { accountsMap } = useBankAccounts();
  const { clientPOs = [] } = useClientPOs();
  const { data: purchasingRequests = [] } = useQuery({
    queryKey: ['purchasing-requests'],
    queryFn: () => base44.entities.PurchasingRequest.list(),
  });

  // Data states
  const [account, setAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('transaksi');
  
  // Filter/Sort states
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Semua status');
  const [dateFilter, setDateFilter] = useState('Semua filter');
  
  // Modals state
  const [isEditAccountOpen, setIsEditAccountOpen] = useState(false);
  const [isDeleteAccountOpen, setIsDeleteAccountOpen] = useState(false);
  const [isTransactionOpen, setIsTransactionOpen] = useState(false);
  const [transactionType, setTransactionType] = useState(''); // 'Terima', 'Kirim', 'Transfer'
  
  // Reconcile states
  const [reconcileModalOpen, setReconcileModalOpen] = useState(false);
  const [selectedForReconcile, setSelectedForReconcile] = useState([]);
  const [statementBalance, setStatementBalance] = useState('');
  
  // New transaction states
  const [newTrxModalOpen, setNewTrxModalOpen] = useState(false);
  const [newTrx, setNewTrx] = useState({ date: '', notes: '', type: 'masuk', amount: '' });
  const [isSaving, setIsSaving] = useState(false);
  
  // Form states
  const [editAccountData, setEditAccountData] = useState({ name: '', category: '' });
  const [trxData, setTrxData] = useState({ date: new Date().toISOString().split('T')[0], amount: '', notes: '', targetAccount: '' });
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('Semua');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterMinAmount, setFilterMinAmount] = useState('');
  const [filterMaxAmount, setFilterMaxAmount] = useState('');

  const resetFilters = () => {
    setFilterType('Semua');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterMinAmount('');
    setFilterMaxAmount('');
    setStatusFilter('Semua status');
  };

  const fetchAccountData = async () => {
    try {
      setLoading(true);
      const data = await base44.entities.ChartOfAccount.list('id');
      const found = data.find(a => a.id === accountId);
      setAccount(found);
      if (found) {
        setEditAccountData({ name: found.name, category: found.category });
      }
      
      // Real COA transactions from AccountMutation
      const allMutations = await base44.entities.AccountMutation.list('-created_date');
      const coaTransactions = [];
      let runningBalance = 0;

      const prefix = accountId ? accountId.charAt(0) : '1';
      // 1: Aset, 5: HPP, 6: Beban, 8: Beban Lain -> Debit bertambah
      const isDebitNormal = ['1', '5', '6', '8'].includes(prefix);
      
      // Khusus untuk akun Bank (1-10002, 1-10003, 1-10004)
      const bankCoaIds = ['1-10002', '1-10003', '1-10004'];
      const isBankAccount = bankCoaIds.includes(accountId);
      
      // Quick read from accountsMap
      let startBalance = 0;
      let targetBankName = '';
      if (isBankAccount) {
        if (accountId === '1-10002') { targetBankName = 'bca'; startBalance = Number(accountsMap.bca?.startBalance) || 0; }
        if (accountId === '1-10003') { targetBankName = 'mandiri'; startBalance = Number(accountsMap.mandiri?.startBalance) || 0; }
        if (accountId === '1-10004') { targetBankName = 'bni'; startBalance = Number(accountsMap.bni?.startBalance) || 0; }
        runningBalance = startBalance;
      }

      // Sort ascending to calculate running balance accurately
      const parseSafeDate = (dateStr) => {
        if (!dateStr) return 0;
        let d;
        if (String(dateStr).includes('/')) {
          const parts = String(dateStr).split('/');
          if (parts.length === 3) {
            d = new Date(`${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`);
          }
        }
        if (!d || isNaN(d.getTime())) d = new Date(dateStr);
        return isNaN(d.getTime()) ? 0 : d.getTime();
      };

      const sortedMutations = [...allMutations].sort((a, b) => parseSafeDate(a.date) - parseSafeDate(b.date));

      // Push Saldo Awal row if it's a Bank Account and startBalance > 0
      if (isBankAccount && startBalance > 0) {
        coaTransactions.push({
          id: 'saldo-awal',
          date: '-', // usually start balance date is before all transactions
          source: 'System',
          notes: 'Saldo Awal Rekening Bank',
          status: 'Selesai',
          debit: startBalance,
          credit: 0,
          balance: startBalance,
          originalType: 'masuk'
        });
      }

      if (accountId === '1-10100' || accountId === '1-10102') {
        const virtualTrx = [];
        clientPOs.forEach(po => {
          let pm = (po.paymentMethod || po.rawRecord?.paymentMethod || '').toLowerCase();
          let shouldInclude = true;
          if (activeBank) {
             shouldInclude = pm.includes(activeBank) || pm.includes(activeNameLower) || pm === activeBank;
          }
          if (!shouldInclude) return;

          let t = 0;
          const items = po.products || po.items || [];
          items.forEach(p => t += (p.total_harga_jual || p.subtotal || p.hargaTotal || 0));
          let leg = 0;
          (po.legalitas || []).forEach(l => leg += (l.harga_jual || 0) * (l.qty_variant || 1));
          const shipping = Number(po.shippingCost || 0);
          const ppnMode = po.ppn_mode || po.rawRecord?.ppn_mode;
          const ppnPercent = parseFloat(po.ppn_percentage || po.rawRecord?.ppn_percentage) || 0;
          const nominalPPN = ppnMode === 'exclude' ? ((t + leg) * ppnPercent / 100) : 0;
          const invoiceTotal = t + leg + shipping + nominalPPN;
          
          const isCustom = po.poMode === 'custom_finance' || po.is_custom_finance;
          const isSample = po.poMode === 'sample' || po.isSample;
          const isCustomOrSample = isCustom || isSample;

          const createdDate = new Date(po.created_date || po.date || Date.now());
          const isTempo = po.rawRecord?.is_tempo || po.rawRecord?.paymentTerms === 'tempo' || po.statusBayar === 'TEMPO';
          let isOverdue = false;
          
          if (isTempo && po.rawRecord?.tempoDate) {
             const tempoDate = new Date(po.rawRecord.tempoDate);
             if (tempoDate < new Date()) isOverdue = true;
          } else {
             const diffTime = Math.abs(new Date() - createdDate);
             const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
             if (diffDays > 40) isOverdue = true;
          }

          if (accountId === '1-10100' && isOverdue) return;
          if (accountId === '1-10102' && !isOverdue) return;

          if (invoiceTotal > 0) {
            virtualTrx.push({
              id: `inv-${po.id}`,
              refId: po.id,
              typeRef: isCustomOrSample ? 'customPO' : 'clientPO',
              date: po.poDate || po.created_date || po.date || new Date().toISOString(),
              source: 'Invoice Klien',
              notes: `[Invoice Generated] ${po.id} - ${po.client || po.customerName || 'Klien / PIC'}`,
              status: 'Selesai',
              debit: invoiceTotal,
              credit: 0,
              originalType: 'masuk',
              isCustom,
              isSample
            });
          }

          (po.riwayatPembayaran || []).forEach((r, idx) => {
             const paidAmt = Number(r.jumlah) || 0;
             if (paidAmt > 0) {
               virtualTrx.push({
                 id: `pay-${po.id}-${idx}`,
                 refId: po.id,
                 typeRef: isCustomOrSample ? 'customPO' : 'clientPO',
                 date: r.tanggal_bayar || r.tanggal || po.poDate || po.created_date,
                 source: 'Pembayaran Klien',
                 notes: `[Pembayaran] ${po.id} - ${po.client || po.customerName || 'Klien / PIC'} - ${r.persentase || 'Cicilan'}`,
                 status: r.status === 'verified' ? 'Selesai' : 'Pending',
                 debit: 0,
                 credit: paidAmt,
                 originalType: 'keluar',
                 isCustom,
                 isSample
               });
             }
          });

        });
        virtualTrx.sort((a, b) => parseSafeDate(a.date) - parseSafeDate(b.date));
        virtualTrx.forEach(trx => {
           runningBalance += (trx.debit - trx.credit);
           trx.balance = runningBalance;
           coaTransactions.push(trx);
        });

      } else if (accountId === '2-20100' || accountId === '2-20101') {
        const virtualTrx = [];
        purchasingRequests.forEach(pr => {
            let shouldInclude = true;
            if (activeBankFilter !== 'Semua') {
               if (activeKey === 'bni') {
                 shouldInclude = pr.ada_faktur_pajak === 'Ya';
               } else if (activeKey === 'bca') {
                 shouldInclude = pr.ada_faktur_pajak !== 'Ya';
               } else {
                 shouldInclude = false;
               }
            }
          if (!shouldInclude) return;

          if (pr.metode_pembayaran === 'Tempo') {
            let isOverdue = false;
            if (pr.jatuh_tempo) {
              const tempoDate = new Date(pr.jatuh_tempo);
              if (tempoDate < new Date()) {
                isOverdue = true;
              }
            }

            if (accountId === '2-20100' && isOverdue) return;
            if (accountId === '2-20101' && !isOverdue) return;

            virtualTrx.push({
              id: `pr-${pr.id}`,
              refId: pr.id,
              typeRef: 'pr-hutang',
              date: pr.created_date,
              source: 'Tagihan Vendor',
              notes: `[Hutang Baru] PR-${pr.id.slice(0,5)} - ${pr.nama_sales || 'Vendor'}`,
              status: 'Selesai',
              debit: 0,
              credit: Number(pr.total_biaya || 0),
              originalType: 'keluar'
            });

            if (['Selesai Dibayar', 'Lunas'].includes(pr.status_pembayaran_tempo) || ['Selesai Dibayar', 'Lunas'].includes(pr.status)) {
              virtualTrx.push({
                 id: `pr-pay-${pr.id}`,
                 refId: pr.id,
                 typeRef: 'pr-hutang',
                 date: pr.updated_date || pr.created_date,
                 source: 'Pelunasan Vendor',
                 notes: `[Pelunasan Hutang] PR-${pr.id.slice(0,5)} - ${pr.nama_sales || 'Vendor'}`,
                 status: 'Selesai',
                 debit: Number(pr.total_biaya || 0),
                 credit: 0,
                 originalType: 'masuk'
              });
            }
          }
        });
        virtualTrx.sort((a, b) => {
          const d1 = parseSafeDate(a.date);
          const d2 = parseSafeDate(b.date);
          return d1 - d2;
        });
        virtualTrx.forEach(trx => {
           runningBalance += (trx.credit - trx.debit);
           trx.balance = runningBalance;
           coaTransactions.push(trx);
        });

      } else {
        for (const mut of sortedMutations) {
          let debit = 0;
          let credit = 0;
          let isValid = false;

          if (isBankAccount) {
             const name = (mut.account || mut.bank_tujuan || '').toLowerCase().trim();
             const bankKey = (mut.bank_key || '').toLowerCase().trim();
             let mutKey = 'bca';
             
             if (bankKey) {
               mutKey = bankKey;
             } else {
               if (name.includes('bca') || name.includes('saja') || name === 'bca satala') mutKey = 'bca';
               else if (name.includes('mandiri') || name.includes('cuan') || name === 'mandiri satala') mutKey = 'mandiri';
               else if (name.includes('bni') || name.includes('essential') || name === 'bni satala') mutKey = 'bni';
             }
             
             if (mutKey === targetBankName) {
               isValid = true;
               const amt = Math.abs(Number(mut.amount || mut.jumlah || 0));
               if (mut.type === 'masuk' || mut.coa_allocations?.[0]?.account_id === '4-40001') debit = amt;
               else if (mut.type === 'keluar') credit = amt;
             }
          } else {
             if (!mut.coa_allocations || !Array.isArray(mut.coa_allocations)) continue;
             const allocation = mut.coa_allocations.find(c => c.account_id === accountId);
             if (allocation) {
               isValid = true;
               const allocAmount = Math.abs(Number(allocation.amount) || 0);
               if (mut.type === 'keluar') {
                 debit = allocAmount;
               } else if (mut.type === 'masuk') {
                 credit = allocAmount;
               } else {
                 debit = allocAmount;
               }
             }
          }
          
          if (isValid) {
            if (isDebitNormal) {
              runningBalance += (debit - credit);
            } else {
              runningBalance += (credit - debit);
            }

            let source = 'Mutasi Kas';
            if (mut.reference_id?.startsWith('prod') || mut.reference_id?.startsWith('bulkprod')) source = 'Biaya Produksi';
            else if (mut.reference_id?.startsWith('opr')) source = 'Biaya Operasional';
            
            coaTransactions.push({
              id: mut.id,
              date: mut.date,
              source: source,
              notes: mut.description,
              status: mut.status || 'Selesai',
              debit,
              credit,
              balance: runningBalance,
              originalType: mut.type,
              typeRef: mut.reference_id?.startsWith('pr-') ? 'pr-hutang' : (mut.reference_id?.startsWith('inv-') || mut.reference_id?.startsWith('pay-') ? 'clientPO' : null),
              refId: mut.reference_id?.replace(/^(pr-|pr-pay-|inv-|pay-|custom-)/, '').split('-')[0] || mut.reference_id
            });
          }
        }
      }
      
      // Reverse to descending for view
      setTransactions(coaTransactions.reverse());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accountId && accountsMap.bca && (!loading || clientPOs.length > 0)) {
       fetchAccountData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, clientPOs, purchasingRequests]);

  // Handlers
  const handleEditAccount = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await base44.entities.ChartOfAccount.update(accountId, {
        name: editAccountData.name,
        category: editAccountData.category
      });
      await fetchAccountData();
      setIsEditAccountOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsSaving(true);
    try {
      await base44.entities.ChartOfAccount.delete(accountId);
      navigate('/office/finance/chart-of-accounts');
    } catch (err) {
      console.error(err);
      alert("Gagal menghapus akun. Mungkin saldo belum 0.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveTransaction = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      // In real app, amount sign depends on type (receive/send/transfer)
      await base44.entities.FinanceTransaction.create({
        accountId,
        type: transactionType,
        date: trxData.date,
        amount: parseFloat(trxData.amount),
        notes: trxData.notes,
        status: 'Selesai'
      });
      await fetchAccountData();
      setIsTransactionOpen(false);
      setTrxData({ date: new Date().toISOString().split('T')[0], amount: '', notes: '', targetAccount: '' });
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const openTransactionModal = (type) => {
    setTransactionType(type);
    setIsTransactionOpen(true);
  };

  // Derived data
  const filteredTransactions = transactions.filter(t => {
    const matchesSearch = t.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'Semua status' || t.status === statusFilter;
    
    const matchesType = filterType === 'Semua' || 
                        (filterType === 'Pemasukan' && (t.debit || 0) > 0) || 
                        (filterType === 'Pengeluaran' && (t.credit || 0) > 0);
                        
    let matchesDate = true;
    if (filterDateFrom || filterDateTo) {
      // Create date objects stripped of time for fair comparison
      const d = new Date(t.date);
      d.setHours(0,0,0,0);
      
      if (filterDateFrom) {
        const fromDate = new Date(filterDateFrom);
        fromDate.setHours(0,0,0,0);
        if (fromDate > d) matchesDate = false;
      }
      if (filterDateTo) {
        const toDate = new Date(filterDateTo);
        toDate.setHours(0,0,0,0);
        if (toDate < d) matchesDate = false;
      }
    }

    let matchesAmount = true;
    const amount = (t.debit || 0) + (t.credit || 0); 
    if (filterMinAmount && amount < Number(filterMinAmount)) matchesAmount = false;
    if (filterMaxAmount && amount > Number(filterMaxAmount)) matchesAmount = false;

    return matchesSearch && matchesStatus && matchesType && matchesDate && matchesAmount;
  });

  const totalDebit = filteredTransactions.reduce((acc, curr) => acc + (curr.debit || 0), 0);
  const totalKredit = filteredTransactions.reduce((acc, curr) => acc + (curr.credit || 0), 0);
  const saldoAkhir = transactions.length > 0 ? transactions[0].balance : 0;

  const formatLedgerDate = (dateStr) => {
    if (!dateStr || dateStr === '-') return '-';
    try {
      let d;
      if (String(dateStr).includes('/')) {
        const parts = String(dateStr).split('/');
        if (parts.length === 3) {
          d = new Date(`${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`);
        }
      }
      
      if (!d || isNaN(d.getTime())) {
        d = new Date(dateStr);
      }
      
      if (!isNaN(d.getTime())) {
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agt", "Sep", "Okt", "Nov", "Des"];
        return `${String(d.getDate()).padStart(2, '0')} ${monthNames[d.getMonth()]} ${d.getFullYear()}`;
      }
    } catch(e) {}
    return String(dateStr).includes('T') ? String(dateStr).split('T')[0] : dateStr;
  };

  return (
    <div className="min-h-[80vh] bg-white border border-[#E6D5B8]/60 rounded-2xl shadow-sm overflow-hidden flex flex-col animate-in fade-in zoom-in duration-500">
      
      {/* Top Header Area */}
      <div className="px-6 pt-6 pb-0 flex flex-col relative z-10">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
          <div className="flex items-start gap-3">
            <div>
              <p className="text-sm text-[#8C6208] font-semibold mb-1 hover:underline cursor-pointer" onClick={() => navigate('/office/finance/chart-of-accounts')}>
                {account ? account.category : 'Kas & bank'}
              </p>
              <h1 className="text-2xl font-black text-[#302621] tracking-tight">
                ({accountId}) - {account ? account.name : 'Loading...'}
              </h1>
            </div>
          </div>

          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-[#E6D5B8] text-[#7A695B] hover:bg-[#F9F5EC] hover:text-[#8C6208] px-4">
                  Tindakan <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="text-[10px] font-semibold text-slate-500 tracking-[0.15em] uppercase">Pengelolaan</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => alert("Fitur segera hadir")} className="cursor-pointer text-sm">Impor rekening koran</DropdownMenuItem>
                <DropdownMenuItem onClick={() => alert("Fitur segera hadir")} className="cursor-pointer text-sm">Hubungkan Cash Link</DropdownMenuItem>
                <DropdownMenuItem onClick={() => alert("Fitur segera hadir")} className="cursor-pointer text-sm">Lihat aturan rekonsiliasi</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] font-semibold text-slate-500 tracking-[0.15em] uppercase">Laporan</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => alert("Fitur segera hadir")} className="cursor-pointer text-sm">Lihat ringkasan rekonsiliasi bank</DropdownMenuItem>
                <DropdownMenuItem onClick={() => alert("Fitur segera hadir")} className="cursor-pointer text-sm">Lihat laporan mutasi rekening koran</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] font-semibold text-slate-500 tracking-[0.15em] uppercase">Akun</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setIsEditAccountOpen(true)} className="cursor-pointer text-sm">Ubah akun</DropdownMenuItem>
                <DropdownMenuItem onClick={() => alert("Fitur segera hadir")} className="cursor-pointer text-sm">Arsipkan akun</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsDeleteAccountOpen(true)} className="cursor-pointer text-sm text-red-600 focus:text-red-600">Hapus akun</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="bg-[#302621] hover:bg-[#4A3D36] text-white shadow-md px-4">
                  Buat transaksi <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => openTransactionModal('Transfer')} className="cursor-pointer text-sm">Transfer uang</DropdownMenuItem>
                <DropdownMenuItem onClick={() => openTransactionModal('Terima')} className="cursor-pointer text-sm">Terima uang</DropdownMenuItem>
                <DropdownMenuItem onClick={() => openTransactionModal('Kirim')} className="cursor-pointer text-sm">Kirim uang</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-6 text-sm font-medium border-b border-[#E6D5B8]/40">
          <button 
            className={`pb-3 border-b-2 transition-colors ${activeTab === 'transaksi' ? 'border-[#8C6208] text-[#8C6208]' : 'border-transparent text-[#7A695B] hover:text-[#302621]'}`}
            onClick={() => setActiveTab('transaksi')}
          >
            Transaksi Jurnal
          </button>
          <button 
            className={`pb-3 border-b-2 transition-colors ${activeTab === 'rekening' ? 'border-[#8C6208] text-[#8C6208]' : 'border-transparent text-[#7A695B] hover:text-[#302621]'}`}
            onClick={() => setActiveTab('rekening')}
          >
            Rekening koran
          </button>
          <button 
            className={`pb-3 border-b-2 transition-colors flex items-center gap-1 ${activeTab === 'rekonsiliasi' ? 'border-[#8C6208] text-[#8C6208]' : 'border-transparent text-[#7A695B] hover:text-[#302621]'}`}
            onClick={() => setActiveTab('rekonsiliasi')}
          >
            Rekonsiliasi <span className="bg-[#A39171] text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center ml-1">0</span>
          </button>
          <button 
            className={`pb-3 border-b-2 transition-colors ${activeTab === 'pemetaan' ? 'border-[#8C6208] text-[#8C6208]' : 'border-transparent text-[#7A695B] hover:text-[#302621]'}`}
            onClick={() => setActiveTab('pemetaan')}
          >
            Pemetaan Kas
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col bg-[#FAFAFA]">
        
        {/* Sub Header Info */}
        <div className="px-6 py-4 flex gap-12 text-sm border-b border-[#E6D5B8]/40 bg-white items-center">
          <div>
            <span className="text-[#7A695B] block mb-1 text-xs">Kategori akun</span>
            <span className="font-semibold text-[#302621]">{account ? account.category : '-'}</span>
          </div>
          <div className="h-10 w-px bg-[#E6D5B8]/40"></div>
          <div>
            <span className="text-[#7A695B] block mb-1 text-xs">Total Debit</span>
            <span className="font-bold text-[#302621]">Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(totalDebit)}</span>
          </div>
          <div className="h-10 w-px bg-[#E6D5B8]/40"></div>
          <div>
            <span className="text-[#7A695B] block mb-1 text-xs">Total Kredit</span>
            <span className="font-bold text-[#302621]">Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(totalKredit)}</span>
          </div>
          <div className="h-10 w-px bg-[#E6D5B8]/40"></div>
          <div>
            <span className="text-[#7A695B] block mb-1 text-xs">Saldo Akhir</span>
            <span className="font-black text-[#8C6208] text-lg">Rp {new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(saldoAkhir)}</span>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="px-6 py-4 flex flex-col md:flex-row gap-4 items-center justify-between border-b border-[#E6D5B8]/40 bg-white">
          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="relative">
              <select 
                value={statusFilter}
                onChange={setStatusFilter}
                className="appearance-none pl-4 pr-8 py-2 text-sm bg-white border border-[#E6D5B8] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30 text-[#302621]"
              >
                <option>Semua status</option>
                <option>Selesai</option>
                <option>Pending</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>

            <Sheet>
              <SheetTrigger asChild>
                <button className="text-[#8C6208] text-sm font-medium hover:underline flex items-center gap-1">
                  Semua filter
                </button>
              </SheetTrigger>
              <SheetContent className="w-[400px] sm:w-[540px] border-l-[#E6D5B8]/60 bg-[#FAFAFA] flex flex-col p-0">
                <SheetHeader className="p-6 border-b border-[#E6D5B8]/40 bg-white">
                  <SheetTitle className="text-xl font-bold text-[#302621]">Filter transaksi</SheetTitle>
                  <SheetDescription className="text-[#7A695B]">
                    Saring data transaksi berdasarkan kriteria spesifik.
                  </SheetDescription>
                </SheetHeader>
                
                <div className="flex-1 overflow-y-auto p-6 space-y-8">
                  {/* Filter Berdasarkan */}
                  <div className="space-y-3">
                    <label className="text-sm font-semibold text-[#302621]">Filter berdasarkan</label>
                    <div className="relative">
                      <select 
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="w-full appearance-none px-4 py-2.5 text-sm bg-white border border-[#E6D5B8] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208] text-[#302621] transition-all shadow-sm hover:border-[#A37B24]">
                        <option>Semua</option>
                        <option>Pemasukan</option>
                        <option>Pengeluaran</option>
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A695B] pointer-events-none" />
                    </div>
                  </div>

                  {/* Tanggal Transaksi */}
                  <div className="space-y-3">
                    <label className="text-sm font-semibold text-[#302621]">Tgl. transaksi</label>
                    <div className="flex items-center gap-3">
                      <div className="relative flex-1">
                        <input 
                          type="date" 
                          value={filterDateFrom}
                          onChange={(e) => setFilterDateFrom(e.target.value)}
                          placeholder="Tanggal mulai" 
                          className="w-full px-4 py-2.5 text-sm bg-white border border-[#E6D5B8] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208] transition-all shadow-sm hover:border-[#A37B24] [&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                        />
                      </div>
                      <span className="text-[#E6D5B8] font-bold">-</span>
                      <div className="relative flex-1">
                        <input 
                          type="date" 
                          value={filterDateTo}
                          onChange={(e) => setFilterDateTo(e.target.value)}
                          placeholder="Tanggal selesai" 
                          className="w-full px-4 py-2.5 text-sm bg-white border border-[#E6D5B8] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208] transition-all shadow-sm hover:border-[#A37B24] [&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Jumlah */}
                  <div className="space-y-3">
                    <label className="text-sm font-semibold text-[#302621]">Jumlah (IDR)</label>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <input 
                          type="number" 
                          value={filterMinAmount}
                          onChange={(e) => setFilterMinAmount(e.target.value)}
                          placeholder="Minimum" 
                          className="w-full px-4 py-2.5 text-sm bg-white border border-[#E6D5B8] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208] transition-all shadow-sm hover:border-[#A37B24]"
                        />
                      </div>
                      <span className="text-[#E6D5B8] font-bold">-</span>
                      <div className="flex-1">
                        <input 
                          type="number" 
                          value={filterMaxAmount}
                          onChange={(e) => setFilterMaxAmount(e.target.value)}
                          placeholder="Maksimum" 
                          className="w-full px-4 py-2.5 text-sm bg-white border border-[#E6D5B8] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208] transition-all shadow-sm hover:border-[#A37B24]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="space-y-3">
                    <label className="text-sm font-semibold text-[#302621]">Status</label>
                    <div className="relative">
                      <select 
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full appearance-none px-4 py-2.5 text-sm bg-white border border-[#E6D5B8] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208] text-[#302621] transition-all shadow-sm hover:border-[#A37B24]">
                        <option>Semua status</option>
                        <option>Selesai</option>
                        <option>Pending</option>
                        <option>Dibatalkan</option>
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A695B] pointer-events-none" />
                    </div>
                  </div>
                </div>

                <SheetFooter className="p-6 border-t border-[#E6D5B8]/40 bg-white sm:justify-between flex-row items-center gap-3">
                  <SheetClose asChild>
                    <Button onClick={resetFilters} variant="outline" className="border-transparent text-[#7A695B] hover:bg-[#F9F5EC] hover:text-[#302621] hover:border-[#E6D5B8] flex-1">
                      Atur Ulang
                    </Button>
                  </SheetClose>
                  <SheetClose asChild>
                    <Button className="bg-[#8C6208] hover:bg-[#A37B24] text-white shadow-md flex-1">
                      Terapkan Filter
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>

          </div>
          
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input 
              type="text" 
              placeholder="Cari transaksi Jurnal" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-[#E6D5B8] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30"
            />
          </div>
        </div>

        {/* Content Body */}
        {filteredTransactions.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-12 text-center bg-[#FAFAFA]">
            <div className="w-48 h-48 mb-6 relative">
              <div className="absolute inset-0 bg-[#F9F5EC] rounded-2xl shadow-inner transform -rotate-6 border border-[#E6D5B8]/40"></div>
              <div className="absolute inset-0 bg-[#FFFDF9] rounded-2xl shadow-inner transform rotate-3 border border-[#E6D5B8]/40"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                 <div className="w-24 h-24 bg-white rounded-full shadow-lg flex items-center justify-center z-10 border border-[#E6D5B8]/60">
                    <span className="text-5xl font-black text-[#8C6208] drop-shadow-md">?</span>
                 </div>
              </div>
            </div>
            <h3 className="text-xl font-bold text-[#302621] mb-2">Belum ada data</h3>
            <p className="text-[#7A695B] max-w-sm">Daftar transaksi Anda akan ditampilkan di sini.</p>
          </div>
        ) : (
          <div className="flex-1 bg-white overflow-x-auto p-6">
            <table className="w-full text-left text-sm table-fixed">
              <thead className="border-b border-[#E6D5B8]/60 text-[#7A695B]">
                <tr>
                  <th className="pb-3 font-semibold w-[12%]">Tanggal</th>
                  <th className="pb-3 font-semibold w-[15%]">Sumber</th>
                  <th className="pb-3 font-semibold w-[25%]">Keterangan</th>
                  <th className="pb-3 pr-4 font-semibold text-right w-[16%]">Debit</th>
                  <th className="pb-3 pr-4 font-semibold text-right w-[16%]">Kredit</th>
                  <th className="pb-3 font-semibold text-right w-[16%]">Saldo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E6D5B8]/30">
                {filteredTransactions
                  .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                  .map(t => (
                  <tr 
                    key={t.id} 
                    className={`hover:bg-[#F9F5EC]/30 ${t.refId ? 'cursor-pointer' : ''}`}
                    onClick={() => {
                      if (t.typeRef === 'clientPO') navigate(`/office/finance/invoices?search=${t.refId}`);
                      if (t.typeRef === 'customPO') navigate(`/office/finance/custom-invoices?search=${t.refId}`);
                      if (t.typeRef === 'pr') navigate('/office/finance/purchasing-requests');
                      if (t.typeRef === 'pr-hutang') navigate(`/office/finance/purchasing-requests?search=${t.refId}`);
                    }}
                  >
                    <td className="p-4 text-xs font-medium text-[#302621]">
                       {formatLedgerDate(t.date)}
                    </td>
                    <td className="py-4 pr-2">
                      <div className="flex flex-col gap-1 items-start">
                        <span className="bg-[#F9F5EC] text-[#8C6208] px-2 py-1 rounded text-xs font-semibold">{t.source}</span>
                        {t.isCustom && <span className="bg-cyan-50 text-cyan-700 border border-cyan-200 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider">Custom</span>}
                        {t.isSample && <span className="bg-purple-50 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider">Sample</span>}
                      </div>
                    </td>
                    <td className="py-4 pr-4 text-[#7A695B] leading-snug">
                       <span className={t.refId ? "text-[#8C6208] hover:underline" : ""}>{t.notes}</span>
                    </td>
                    <td className="py-4 pr-4 text-right font-mono font-medium text-[#302621]">
                      {t.debit > 0 ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(t.debit) : '-'}
                    </td>
                    <td className="py-4 pr-4 text-right font-mono font-medium text-[#302621]">
                      {t.credit > 0 ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(t.credit) : '-'}
                    </td>
                    <td className="py-4 text-right font-mono font-bold text-[#8C6208]">
                      {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(t.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredTransactions.length > 0 && (
          <div className="p-4 border-t border-[#E6D5B8]/40 flex items-center justify-between bg-white rounded-b-2xl">
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#7A695B]">Row per Page</span>
              <Select 
                value={String(rowsPerPage)} 
                onValueChange={(val) => {
                  setRowsPerPage(Number(val));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-[70px] h-8 bg-white border-[#E6D5B8] focus:ring-[#8C6208]">
                  <SelectValue placeholder={rowsPerPage} />
                </SelectTrigger>
                <SelectContent side="top">
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-[#7A695B]">
                {(currentPage - 1) * rowsPerPage + 1} - {Math.min(currentPage * rowsPerPage, filteredTransactions.length)} of {filteredTransactions.length}
              </span>
              <div className="flex items-center gap-1">
                <button 
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-[#F9F5EC] disabled:opacity-50 text-[#7A695B]"
                >
                  &lt;
                </button>
                <button 
                  onClick={() => setCurrentPage(Math.min(Math.ceil(filteredTransactions.length / rowsPerPage), currentPage + 1))}
                  disabled={currentPage >= Math.ceil(filteredTransactions.length / rowsPerPage)}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-[#F9F5EC] disabled:opacity-50 text-[#7A695B]"
                >
                  &gt;
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Modals */}
      
      {/* Ubah Akun */}
      <Dialog open={isEditAccountOpen} onOpenChange={setIsEditAccountOpen}>
        <DialogContent className="sm:max-w-[900px] bg-[#FFFDF9] border-[#E6D5B8] p-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E6D5B8]/40 bg-white">
            <h2 className="text-xl font-bold text-[#302621]">Ubah akun</h2>
          </div>
          
          <div className="max-h-[70vh] overflow-y-auto px-6 py-6">
            <form id="edit-account-form" onSubmit={handleEditAccount} className="space-y-8">
              {/* Informasi Akun Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1.5 h-4 bg-[#8C6208] rounded-full"></div>
                  <h3 className="font-semibold text-[#302621]">Informasi akun</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                  {/* Kiri */}
                  <div className="space-y-5">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-[#302621] flex justify-between">
                        <span>Nama akun <span className="text-red-500">*</span></span>
                        <span className="text-slate-400 text-xs">0/100</span>
                      </label>
                      <input 
                        type="text" 
                        value={editAccountData.name}
                        onChange={(e) => setEditAccountData({...editAccountData, name: e.target.value})}
                        className="w-full px-3 py-2 border border-[#E6D5B8] rounded-lg focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208] text-[#302621] outline-none transition-all"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-[#302621]">Kategori Akun <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <select 
                          value={editAccountData.category}
                          onChange={(e) => setEditAccountData({...editAccountData, category: e.target.value})}
                          className="w-full appearance-none pl-3 pr-8 py-2 border border-[#E6D5B8] rounded-lg focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208] bg-white text-[#302621] outline-none transition-all"
                        >
                          <option value="Cash & Bank">Cash & Bank</option>
                          <option value="Accounts Receivable (A/R)">Accounts Receivable (A/R)</option>
                          <option value="Other Current Asset">Other Current Asset</option>
                          <option value="Fixed Asset">Fixed Asset</option>
                          <option value="Current Liability">Current Liability</option>
                          <option value="Equity">Equity</option>
                          <option value="Income">Income</option>
                          <option value="Expense">Expense</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-[#302621]">Sub-akun dari</label>
                      <div className="relative">
                        <select className="w-full appearance-none pl-3 pr-8 py-2 border border-[#E6D5B8] rounded-lg focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208] bg-white text-[#302621] outline-none transition-all text-slate-400">
                          <option>Pilih akun</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-[#302621]">Saldo awal</label>
                      <div className="relative">
                        <select className="w-full appearance-none pl-3 pr-8 py-2 border border-[#E6D5B8] rounded-lg focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208] bg-white text-[#302621] outline-none transition-all text-slate-400">
                          <option>Pilih akun</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-[#302621] flex justify-between">
                        <span>Keterangan</span>
                        <span className="text-slate-400 text-xs">0/200</span>
                      </label>
                      <textarea 
                        rows="3"
                        placeholder="Contoh: Kas untuk modal awal perusahaan..."
                        className="w-full px-3 py-2 border border-[#E6D5B8] rounded-lg focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208] text-[#302621] outline-none transition-all"
                      ></textarea>
                    </div>
                  </div>

                  {/* Kanan */}
                  <div className="space-y-5">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-[#302621] flex justify-between">
                        <span>Kode akun <span className="text-red-500">*</span></span>
                        <span className="text-slate-400 text-xs">0/20</span>
                      </label>
                      <input 
                        type="text" 
                        value={accountId}
                        disabled
                        className="w-full px-3 py-2 border border-[#E6D5B8] rounded-lg bg-slate-50 text-slate-500 outline-none cursor-not-allowed"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Akses Akun Section */}
              <div className="space-y-4 pt-4 border-t border-[#E6D5B8]/40">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1.5 h-4 bg-[#8C6208] rounded-full"></div>
                  <h3 className="font-semibold text-[#302621]">Akses akun</h3>
                </div>
                
                <div className="space-y-3">
                  <p className="text-sm text-slate-500">Pengguna yang dapat mengakses ini:</p>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="access" className="accent-[#8C6208]" defaultChecked />
                      <span className="text-sm text-[#302621]">Semua pengguna</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="access" className="accent-[#8C6208]" />
                      <span className="text-sm text-[#302621]">Beberapa pengguna</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="access" className="accent-[#8C6208]" />
                      <span className="text-sm text-[#302621]">Hanya pembuat</span>
                    </label>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <div className="px-6 py-4 border-t border-[#E6D5B8]/40 bg-white flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setIsEditAccountOpen(false)} className="border-transparent text-[#7A695B] hover:bg-[#F9F5EC] hover:text-[#302621]">
              Batalkan
            </Button>
            <Button form="edit-account-form" type="submit" disabled={isSaving} className="bg-[#8C6208] text-white hover:bg-[#A37B24]">
              {isSaving ? 'Menyimpan...' : 'Simpan perubahan'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hapus Akun */}
      <Dialog open={isDeleteAccountOpen} onOpenChange={setIsDeleteAccountOpen}>
        <DialogContent className="sm:max-w-[425px] bg-[#FFFDF9] border-[#E6D5B8]">
          <DialogHeader>
            <DialogTitle className="text-red-600">Hapus Akun?</DialogTitle>
            <DialogDescription className="text-[#7A695B]">
              Apakah kamu yakin ingin menghapus akun ini secara permanen?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setIsDeleteAccountOpen(false)}>Batal</Button>
            <Button onClick={handleDeleteAccount} disabled={isSaving} className="bg-red-600 hover:bg-red-700 text-white">Hapus Permanen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Buat Transaksi (Terima/Kirim/Transfer) */}
      <Dialog open={isTransactionOpen} onOpenChange={setIsTransactionOpen}>
        <DialogContent className="sm:max-w-[425px] bg-[#FFFDF9] border-[#E6D5B8]">
          <DialogHeader>
            <DialogTitle className="text-[#302621]">{transactionType} Uang</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTransaction} className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#302621]">Tanggal Transaksi</label>
              <input 
                type="date" 
                value={trxData.date}
                onChange={(e) => setTrxData({...trxData, date: e.target.value})}
                className="w-full px-3 py-2 border border-[#E6D5B8] rounded-lg focus:ring-2 focus:ring-[#8C6208]/30 text-[#302621]"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#302621]">Jumlah (IDR)</label>
              <input 
                type="number" 
                min="0"
                value={trxData.amount}
                onChange={(e) => setTrxData({...trxData, amount: e.target.value})}
                className="w-full px-3 py-2 border border-[#E6D5B8] rounded-lg focus:ring-2 focus:ring-[#8C6208]/30 text-[#302621]"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#302621]">Keterangan</label>
              <textarea 
                rows="2"
                value={trxData.notes}
                onChange={(e) => setTrxData({...trxData, notes: e.target.value})}
                className="w-full px-3 py-2 border border-[#E6D5B8] rounded-lg focus:ring-2 focus:ring-[#8C6208]/30 text-[#302621]"
                required
              ></textarea>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsTransactionOpen(false)}>Batal</Button>
              <Button type="submit" disabled={isSaving} className="bg-[#302621] hover:bg-[#4A3D36] text-white">Simpan Transaksi</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
